import {
  createStyleMixin,
  draggableMixin,
  createProxy,
  proxyInputMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import {
  getComponentName,
  forwardAttrs,
  syncAttrs,
} from '@descope-ui/common/components-helpers';
import { compose } from '@descope-ui/common/utils';
import { ComboBoxClass } from '@descope-ui/descope-combo-box/class';
import { componentName as descopeInternalComponentName } from './descope-country-subdivision-city-field-internal/CountrySubdivisionCityFieldInternal';

export const componentName = getComponentName('country-subdivision-city-field');

const customMixin = (superclass) =>
  class CountrySubdivisionCityFieldMixinClass extends superclass {
    init() {
      super.init?.();

      const template = document.createElement('template');

      template.innerHTML = `
      <${descopeInternalComponentName}
        tabindex="-1"
      ></${descopeInternalComponentName}>
      `;

      this.baseElement.appendChild(template.content.cloneNode(true));

      this.inputElement = this.shadowRoot.querySelector(
        descopeInternalComponentName,
      );

      forwardAttrs(this, this.inputElement, {
        includeAttrs: [
          'size',
          'bordered',
          'required',
          'label-type',
          'disabled',
          'readonly',
          'hide-country',
          'hide-subdivision',
          'hide-city',
          'default-country',
          'allowed-countries',
          'pinned-countries',
          'country-label',
          'subdivision-label',
          'city-label',
          'country-placeholder',
          'subdivision-placeholder',
          'city-placeholder',
          'data-source-base-url',
          'lang',
          'invalid',
        ],
      });

      // Sync invalid attribute bidirectionally so external validation feedback propagates
      syncAttrs(this, this.inputElement, { includeAttrs: ['invalid'] });
    }
  };

const host = { selector: () => ':host' };

const internalWrapper = {
  selector: `${descopeInternalComponentName} > .wrapper`,
};

const internalComboBoxes = {
  selector: `${descopeInternalComponentName} > .wrapper > descope-combo-box`,
};

export const CountrySubdivisionCityFieldClass = compose(
  createStyleMixin({
    componentNameOverride: getComponentName('input-wrapper'),
  }),
  createStyleMixin({
    mappings: {
      hostWidth: { ...host, property: 'width' },
      hostDirection: [
        { ...host, property: 'direction' },
        {
          ...internalComboBoxes,
          property: ComboBoxClass.cssVarList.hostDirection,
        },
      ],
      flexDirection: { ...internalWrapper, property: 'flex-direction' },
      flexGap: { ...internalWrapper, property: 'gap' },
      itemAlignment: { ...internalWrapper, property: 'align-items' },
      itemMinWidth: { ...internalComboBoxes, property: 'min-width' },
      itemFlexGrow: { ...internalComboBoxes, property: 'flex-grow' },
      itemWidth: {
        ...internalComboBoxes,
        property: ComboBoxClass.cssVarList.hostWidth,
      },
    },
  }),
  draggableMixin,
  proxyInputMixin({
    proxyProps: ['value'],
    inputEvent: 'input',
    proxyParentValidation: true,
  }),
  componentNameValidationMixin,
  customMixin,
)(
  createProxy({
    slots: [],
    wrappedEleName: 'vaadin-custom-field',
    style: () => `
  :host {
    display: inline-flex;
    max-width: 100%;
  }
  vaadin-custom-field {
    line-height: unset;
    width: 100%;
  }

  ${descopeInternalComponentName} {
    display: inline-block;
    box-sizing: border-box;
    user-select: none;
    width: 100%;
    max-width: 100%;
  }

  ${descopeInternalComponentName} > .wrapper {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
  }

  .${descopeInternalComponentName}-hidden {
    display: none;
  }

`,
    excludeAttrsSync: ['tabindex', 'style', 'error-message'],
    componentName,
  }),
);
